import { useContext } from 'react';
import { BootstrapStateContext } from '../../../bootstrap/bootstrap-provider';
import { useAdditionalInfos } from '../use-additional-infos/additional-infos-provider';

/**
 * Fallback cap used only if the bootstrap value is unavailable (an older server
 * that predates the field, or a render tree without a bootstrap provider such
 * as a component test). Mirrors the backend default for
 * `cad.narrative.pinned_messages_limit`. The backend enforces the real cap
 * regardless — this value only drives the client-side disabled affordance.
 */
export const DEFAULT_MAX_PINNED_NARRATIVES = 10;

export interface UsePinCapResult {
    /** True when the event has hit (or exceeded) its pinned-narrative cap. */
    isPinCapReached: boolean;
    /** Hover copy for the disabled Pin button. Undefined when the cap is not reached. */
    pinCapTooltip: string | undefined;
}

/**
 * Builds the "Pin limit reached" hover copy.
 *
 * `z` is the number of pins to remove to make room for one more, i.e.
 * `pinnedCount - maxPinned + 1`. At the exact cap that is 1 ("a message"); an
 * event merge can push the count past the cap, in which case more must be
 * unpinned. The count/max pair (y/x) is always shown so the dispatcher can see
 * how far over they are.
 */
function buildPinCapTooltip(pinnedCount: number, maxPinned: number): string {
    const toUnpin = pinnedCount - maxPinned + 1;
    const unpinPhrase = toUnpin <= 1 ? 'a message' : `${toUnpin} messages`;
    return `Pin limit reached (${pinnedCount}/${maxPinned}). Unpin ${unpinPhrase} to add another.`;
}

export function usePinCap(): UsePinCapResult {
    const bootstrap = useContext(BootstrapStateContext);
    const { pinnedEntries } = useAdditionalInfos();

    const maxPinned = bootstrap?.maxPinnedNarrativesLimit ?? DEFAULT_MAX_PINNED_NARRATIVES;
    const pinnedCount = pinnedEntries.length;
    const isPinCapReached = pinnedCount >= maxPinned;

    return {
        isPinCapReached,
        pinCapTooltip: isPinCapReached ? buildPinCapTooltip(pinnedCount, maxPinned) : undefined,
    };
}
